/**
 * Editable email preview.
 *
 * Renders the invite email exactly as the recipient will see it (colours,
 * layout, activation button) — but the whole body is `contentEditable`, so
 * the firm can tweak wording inline before hitting Resend. Subject sits in
 * a regular text input above.
 *
 * Exposes an imperative handle so the parent can pull the current values
 * when the user confirms:
 *
 *     const editorRef = useRef();
 *     ...
 *     <EditableEmailPreview ref={editorRef} initialSubject={...} initialHtml={...} />
 *     ...
 *     const { subject, html } = editorRef.current.getValues();
 *
 * We intentionally avoid a heavyweight WYSIWYG library: the invite email is
 * a fixed HTML fragment, and native contentEditable handles the 95% case
 * (text edits, delete/insert paragraphs, formatting via keyboard shortcuts)
 * without any dependency footprint.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Bold, Italic, Undo2, RotateCcw, Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const EditableEmailPreview = forwardRef(function EditableEmailPreview(
    { initialSubject, initialHtml, toEmail, activationHint },
    ref,
) {
    const [subject, setSubject] = useState(initialSubject || "");
    const bodyRef = useRef(null);
    const [dirty, setDirty] = useState(false);

    // Set initial HTML exactly once. React can't re-render into a contentEditable
    // node without wrecking the caret, so we imperatively poke it in.
    useEffect(() => {
        if (bodyRef.current && initialHtml != null) {
            bodyRef.current.innerHTML = initialHtml;
            setDirty(false);
        }
    }, [initialHtml]);

    useEffect(() => { setSubject(initialSubject || ""); }, [initialSubject]);

    useImperativeHandle(ref, () => ({
        getValues: () => ({
            subject: subject.trim(),
            html: bodyRef.current?.innerHTML || "",
        }),
        reset: () => {
            setSubject(initialSubject || "");
            if (bodyRef.current) bodyRef.current.innerHTML = initialHtml || "";
            setDirty(false);
        },
    }));

    function exec(cmd) {
        document.execCommand(cmd, false, null);
        bodyRef.current?.focus();
        setDirty(true);
    }

    return (
        <div className="flex-1 overflow-hidden flex flex-col gap-3 min-h-0">
            <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-2 items-center text-sm">
                <span className="text-[#5E636E] text-xs">To</span>
                <span className="font-mono text-xs truncate">{toEmail || "—"}</span>
                <Label htmlFor="edit-subject" className="text-[#5E636E] text-xs">Subject</Label>
                <Input
                    id="edit-subject"
                    value={subject}
                    onChange={(e) => { setSubject(e.target.value); setDirty(true); }}
                    className="h-8 text-sm"
                    data-testid="editable-preview-subject"
                />
            </div>

            <div className="flex items-center gap-1 border border-b-0 border-[#E4E4E7] rounded-t-md px-2 py-1 bg-[#FAFAFA]">
                <button type="button" onClick={() => exec("bold")} className="p-1 rounded hover:bg-zinc-200" title="Bold (Ctrl+B)" aria-label="Bold" data-testid="edit-bold"><Bold className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => exec("italic")} className="p-1 rounded hover:bg-zinc-200" title="Italic (Ctrl+I)" aria-label="Italic" data-testid="edit-italic"><Italic className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => exec("undo")} className="p-1 rounded hover:bg-zinc-200" title="Undo (Ctrl+Z)" aria-label="Undo" data-testid="edit-undo"><Undo2 className="h-3.5 w-3.5" /></button>
                <span className="mx-1 text-[#E4E4E7]">·</span>
                <button
                    type="button"
                    onClick={() => { if (bodyRef.current) bodyRef.current.innerHTML = initialHtml || ""; setSubject(initialSubject || ""); setDirty(false); }}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] text-[#5E636E] hover:bg-zinc-200"
                    title="Discard your edits and go back to the original template"
                    data-testid="edit-reset"
                >
                    <RotateCcw className="h-3 w-3" /> Reset
                </button>
                <span className={"ml-auto text-[10px] " + (dirty ? "text-emerald-700 font-medium" : "text-[#5E636E]")}>
                    {dirty ? "Edited" : "Original template"}
                </span>
            </div>
            <div
                ref={bodyRef}
                contentEditable
                suppressContentEditableWarning
                onInput={() => setDirty(true)}
                spellCheck={true}
                className="flex-1 min-h-[380px] w-full overflow-auto rounded-b-md border border-[#E4E4E7] bg-white px-6 py-4 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                data-testid="editable-preview-body"
            />
            <div className="flex items-start gap-1.5 text-[11px] text-[#5E636E]">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                <span>
                    Edit text inline (or use <kbd className="px-1 border rounded bg-white text-[10px]">Ctrl</kbd>
                    +<kbd className="px-1 border rounded bg-white text-[10px]">B</kbd>/<kbd className="px-1 border rounded bg-white text-[10px]">I</kbd>).
                    {activationHint ? ` ${activationHint} ` : " "}
                    Any activation link inside your edits is automatically re-pointed to the fresh single-use token when you hit Resend.
                </span>
            </div>
        </div>
    );
});

export default EditableEmailPreview;
